import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../ApplicationState';
import { getPublicMediaRoot } from '../../serviceAgent';
import { play, pause, setMedia, State } from '../AudioPlayer/AudioPlayer';
import Button from '../Button';
import { Icon } from '@iconify/react';
import styles from './ToDoFeaturedStory.module.scss';
import { getGradeAsNumber } from '../../GlobalFunctions';

const baseAudioUrlMS = 'https://s3.amazonaws.com/peekaplatform/assets/premium/storyAudioMiddleSchool/';
const baseAudioUrlHS = 'https://s3.amazonaws.com/peekaplatform/assets/premium/storyAudioHighSchool/';

interface urlParams {
  activity: string;
  grade: string;
}

interface Props {
  content: {
    title: string;
    content: string[];
    id: string;
    representativeImage: string;
    grade: number[];
  };
  handleTabClick: (tab: number) => void;
  nextTab: number;
  isLastTab: boolean;
}

interface LoadAudioParams {
  base: string;
  storyId: string;
}

const ToDoFeaturedStory = (props: Props): JSX.Element => {
  const { content, handleTabClick, isLastTab, nextTab } = props;

  const [isPlaying, setIsPlaying] = useState(false);
  const { activity, grade }: urlParams = useParams();

  const gradeAsNumber = getGradeAsNumber(grade);

  const dispatch = useDispatch();

  const isHighSchool = gradeAsNumber >= 9 && gradeAsNumber <= 12;

  const { media, isPlayerLoading } = useSelector((state: RootState) => ({
    media: state.audioPlayer.media,
    isPlayerLoading: state.audioPlayer.state === State.loading,
  }));

  const imagePath = content?.representativeImage
    ? `${getPublicMediaRoot()}images/secondary/${content?.representativeImage}`
    : `${getPublicMediaRoot()}images/secondary/ToDoList Header_Story Assignment.png`;

  const isCurrentStoryMediaLoaded = () => media?.title === content.title;

  function getAudioUrl(params: LoadAudioParams) {
    const { base, storyId } = params;
    const storyFileName = `${storyId}-story.wav`;
    return `${base}${storyFileName}`;
  }

  const generateAudioPointer = (params: LoadAudioParams) => {
    const { base, storyId } = params;
    return {
      sources: { mp3: getAudioUrl({ base, storyId }) },
      title: storyId,
    };
  };

  const setStoryMedia = () => {
    dispatch(
      setMedia(
        generateAudioPointer({
          base: !isHighSchool ? baseAudioUrlMS : baseAudioUrlHS,
          storyId: !isHighSchool ? activity : activity,
        }),
      ),
    );
  };

  const handlePlayOrPauseAudio = () => {
    if (!isPlaying) {
      if (!isCurrentStoryMediaLoaded()) {
        setStoryMedia();
      }
      setIsPlaying(true);
      dispatch(play());
    } else {
      setIsPlaying(false);
      dispatch(pause());
    }
  };

  useEffect(() => {
    setStoryMedia();
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.blueBand} />
      <div className={styles.storyContainer}>
        <img src={imagePath} alt='' />
        <div className={styles.titleContainer}>
          <h2>{content.title}</h2>
        </div>
        {media && (
          <PlayerControls
            pauseState={!isPlaying}
            handlePlayOrPauseAudio={handlePlayOrPauseAudio}
            isPlayerLoading={isPlayerLoading}
          />
        )}
        {content.content.map((textBlock, index) => {
          return (
            <div
              key={`featured story part ${index}`}
              className={styles.textBlock}
              dangerouslySetInnerHTML={{ __html: textBlock }}
            />
          );
        })}
        {!isLastTab && (
          <div className={styles.buttonContainer}>
            <Button type='submit' darkBlue thinText onClick={() => handleTabClick(nextTab)}>
              Start Activity&nbsp;&nbsp;&rarr;
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface PlayerControlsProps {
  pauseState: boolean;
  handlePlayOrPauseAudio: () => void;
  isPlayerLoading: boolean;
}

function PlayerControls(props: PlayerControlsProps) {
  const { pauseState, handlePlayOrPauseAudio, isPlayerLoading } = props;
  return (
    <div className={styles.audioPlayerButtonContainer}>
      <Button onClick={handlePlayOrPauseAudio} disabled={isPlayerLoading}>
        <Icon className={styles.audioPlayerIcon} icon={pauseState ? 'ph:play' : 'ph:pause'} />
        <span>{pauseState ? 'play' : 'pause'} audio</span>
      </Button>
    </div>
  );
}

export default ToDoFeaturedStory;
